type PosthogInitState = 'pending' | 'initialized' | 'skipped'

let initState: PosthogInitState = 'pending'
const settledListeners = new Set<(state: 'initialized' | 'skipped') => void>()

const settle = (next: 'initialized' | 'skipped') => {
  if (initState !== 'pending') {
    return
  }
  initState = next
  settledListeners.forEach((listener) => listener(next))
  settledListeners.clear()
}

const onPosthogSettled = (
  listener: (state: 'initialized' | 'skipped') => void,
): (() => void) => {
  if (initState !== 'pending') {
    listener(initState)
    return () => undefined
  }
  settledListeners.add(listener)
  return () => {
    settledListeners.delete(listener)
  }
}

/** True only after `posthog.init()` has run. False when init was skipped. */
export const isPosthogReady = () => initState === 'initialized'

/** True when the region has no key, so flags resolve as off without the SDK. */
export const isPosthogSkipped = () => initState === 'skipped'

/**
 * Announces that `posthog.init()` has run. Each app owns its own init; this is
 * the signal flag readers wait on before calling `posthog.onFeatureFlags`.
 */
export const markPosthogReady = () => settle('initialized')

/**
 * Announces that PostHog will never initialize (no `phc_` key for the region).
 * Flag readers treat this as ready + disabled so fallback paths still fetch.
 * Do not call this when init is only deferred (e.g. user id has not landed).
 */
export const markPosthogSkipped = () => settle('skipped')

/**
 * Runs the listener once PostHog initializes — immediately if it already has.
 * Does not fire when init was skipped. Returns an unsubscribe function.
 */
export const onPosthogReady = (listener: () => void): (() => void) =>
  onPosthogSettled((state) => {
    if (state === 'initialized') listener()
  })

/**
 * Runs the listener once init is skipped — immediately if it already was.
 * Returns an unsubscribe function.
 */
export const onPosthogSkipped = (listener: () => void): (() => void) =>
  onPosthogSettled((state) => {
    if (state === 'skipped') listener()
  })
